/**
 * v2.51 — Anticipo (customer advance payment) API.
 *
 * CSRF pattern mirrors payments.ts: read the `pacebi_csrf` cookie and echo it
 * back via `X-CSRF-Token` on every state-changing POST.
 */
import type {
  RecordAnticipoResponse,
  ReverseAnticipoResponse,
} from './types';

function readCookie(name: string): string | null {
  const m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
}

function csrfHeaders(): Record<string, string> {
  const csrf = readCookie('pacebi_csrf');
  const h: Record<string, string> = { 'Content-Type': 'application/json' };
  if (csrf) h['X-CSRF-Token'] = csrf;
  return h;
}

async function postJSON<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(path, {
    method: 'POST',
    credentials: 'same-origin',
    headers: csrfHeaders(),
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

/**
 * Record a new anticipo. Generates a fresh `idempotency_key` via
 * `crypto.randomUUID()` so retries from the same panel render won't
 * double-post.
 */
export function recordAnticipo(params: {
  sid: number;
  anum: number;
  amount: number;
  cash_code: string;
  reference: string;
}): Promise<RecordAnticipoResponse> {
  return postJSON<RecordAnticipoResponse>('/api/anticipos', {
    ...params,
    idempotency_key: crypto.randomUUID(),
  });
}

/**
 * Reverse an existing anticipo by its trankey. `sid` is the account's store —
 * the daemon reads it from the params to locate the payment.
 */
export function reverseAnticipo(sid: number, payment_trankey: number): Promise<ReverseAnticipoResponse> {
  return postJSON<ReverseAnticipoResponse>('/api/anticipos/reverse', {
    sid,
    payment_trankey,
    idempotency_key: crypto.randomUUID(),
  });
}
